/**
 * The step-coverage vocabulary shared by every Explorer map visual: the
 * states, what they are called, and the token classes that draw them.
 *
 * A state counts the applications documented at a step and nothing more. It
 * does not say whether several applications overlap or complement each other,
 * and an undocumented step is not a gap: the map only records what has been
 * documented.
 * So "multiple" takes a neutral accent, not a decision color.
 */

export type CoverageState = "single" | "multiple" | "none";

export type CoverageCounts = Record<CoverageState, number>;

/** Reading order for bars, rings, and legends. */
export const COVERAGE_STATES: readonly CoverageState[] = [
  "single",
  "multiple",
  "none",
];

export const COVERAGE_LABEL: Record<CoverageState, string> = {
  single: "One application",
  multiple: "Multiple applications",
  none: "No documented support",
};

export const COVERAGE_MEANING: Record<CoverageState, string> = {
  single: "One application has documented support at the step.",
  multiple: "Two or more applications have documented support at the step.",
  none: "No application has documented support at the step yet.",
};

/** Bar segments and filled pills. */
export const COVERAGE_FILL_CLASS: Record<CoverageState, string> = {
  single: "bg-(--status-success)",
  multiple: "bg-(--accent-cobalt)",
  none: "bg-(--fg-subtle)",
};

/** The left edge of a step card. */
export const COVERAGE_EDGE_CLASS: Record<CoverageState, string> = {
  single: "border-l-(--status-success)",
  multiple: "border-l-(--accent-cobalt)",
  none: "border-l-(--fg-subtle)",
};

/** Ring arcs. */
export const COVERAGE_STROKE_CLASS: Record<CoverageState, string> = {
  single: "stroke-(--status-success)",
  multiple: "stroke-(--accent-cobalt)",
  none: "stroke-(--fg-subtle)",
};

export function coverageTotal(coverage: CoverageCounts): number {
  return coverage.single + coverage.multiple + coverage.none;
}

/** A sentence for the accessible name of a coverage chart. */
export function describeCoverage(coverage: CoverageCounts): string {
  const parts = COVERAGE_STATES.map(
    (state) => `${coverage[state]} ${COVERAGE_LABEL[state].toLowerCase()}`,
  );
  return `${coverageTotal(coverage)} steps: ${parts.join(", ")}`;
}
